// src/App.js
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes } from "react-router-dom";
import { fetchUserSettings } from "./store/settingsSlice";
import SignupFormPage from "./components/SignupForm";
import LoginFormPage from "./components/LoginForm";
import { authenticate } from "./store/session";
import Navigation from "./components/Navigation";
import HomePage from "./components/HomePage";
import CompareBibles from "./components/CompareBibles";
import BibleDisplay from "./components/BibleDisplay";
import MyMemos from "./components/MyResources";
import NotesPage from "./components/NotesPage";
import HighlightsPage from "./components/HighlightsPage";
import SermonsPage from "./components/SermonsPage";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import LoginRedirectPage from "./components/LoginRedirectPage";
import SearchResults from "./components/SearchResults";
import Settings from "./components/Settings";
import AboutApp from "./components/AboutApp";
import WhoWeAre from "./components/WhoWeAre";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import RoleRoute from "./components/auth/RoleRoute";
import DailyDevotionPage from "./components/devotions/DailyDevotionPage";
import DevotionHistoryPage from "./components/devotions/DevotionHistoryPage";
import PlanModeReader from "./components/reading-plans/PlanModeReader";
import ReadingPlanOverview from "./components/reading-plans/ReadingPlanOverview";
import ReadingPlansHub from "./components/reading-plans/ReadingPlansHub";
import { ReadingPlanAdminList, ReadingPlanAdminDetail } from "./components/admin/ReadingPlanAdmin";
import SundayPreparationPage from "./components/devotions/SundayPreparationPage";
import PastorDevotionDashboard from "./components/devotions/PastorDevotionDashboard";
import PastoralContentEditor from "./components/devotions/PastoralContentEditor";
import { useTranslation } from "react-i18next";

function App() {
  const dispatch = useDispatch();
  const [isLoaded, setIsLoaded] = useState(false);
  const { t, i18n } = useTranslation();
  const user = useSelector((state) => state.session.user);
  const settings = useSelector((state) => state.settings);

  useEffect(() => {
    // Load saved language preference if available
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
    // Authenticate user/session before rendering the app
    dispatch(authenticate())
      .catch((error) => {
        console.error("Session authentication failed:", error);
      })
      .finally(() => setIsLoaded(true));
  }, [dispatch, i18n]);

  // Pull user settings as soon as we know who the user is, so that
  // defaultLanguage / defaultVersion are globally available (not lazy per
  // page) and the UI language reflects the server-side preference on fresh
  // browsers / after sign-in on a new device.
  useEffect(() => {
    if (user && !settings.isLoaded && !settings.loading) {
      dispatch(fetchUserSettings());
    }
  }, [dispatch, user, settings.isLoaded, settings.loading]);

  // Server is the source of truth for the UI language once settings load.
  // If it differs from the current i18n language, apply it and keep the
  // localStorage mirror in sync so a reload picks the same value.
  useEffect(() => {
    if (!settings.isLoaded || !settings.defaultLanguage) return;
    if (settings.defaultLanguage !== i18n.language) {
      i18n.changeLanguage(settings.defaultLanguage);
      try {
        localStorage.setItem("language", settings.defaultLanguage);
      } catch (_e) {
        // Ignore storage errors (private mode etc.).
      }
    }
  }, [settings.isLoaded, settings.defaultLanguage, i18n]);

  if (!isLoaded) {
    // Display loading message using translation
    return <div>{t("loading")}</div>;
  }

  return (
    <div 
      className="app-container min-h-screen bg-cover bg-center"
    >
      <Navigation isLoaded={isLoaded} />

     
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/bible-reading" element={<BibleDisplay />} />
        <Route path="/bible/:version/:book/:chapter" element={<BibleDisplay />} />
        <Route path="/bible-reading/plan/:planSlug/:date" element={<PlanModeReader />} />
        <Route path="/reading-plans" element={<ReadingPlansHub />} />
        <Route path="/reading-plans/:planSlug" element={<ReadingPlanOverview />} />
        <Route path="/devotions/today" element={<DailyDevotionPage />} />
        <Route path="/devotions/date/:date" element={<DailyDevotionPage />} />
        <Route path="/login" element={<LoginFormPage />} />
        <Route path="/login-redirect" element={<LoginRedirectPage />} />
        <Route path="/signup" element={<SignupFormPage />} />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/version-comparison" element={<CompareBibles />} />
        <Route path="/my-memos" element={<MyMemos />} />
        <Route path="/my-memos/memos" element={<NotesPage />} />
        <Route path="/my-memos/highlights" element={<HighlightsPage />} />
        <Route path="/my-memos/sermons" element={<SermonsPage />} />
        <Route path="/my-memos/devotions" element={<ProtectedRoute><DevotionHistoryPage /></ProtectedRoute>} />
        <Route path="/devotions/sunday" element={<ProtectedRoute><SundayPreparationPage /></ProtectedRoute>} />
        <Route path="/devotions/sunday/:weekStart" element={<ProtectedRoute><SundayPreparationPage /></ProtectedRoute>} />
        <Route path="/admin/devotions" element={<RoleRoute roles={["super_admin"]}><PastorDevotionDashboard /></RoleRoute>} />
        <Route path="/admin/devotions/:dailyPassageId/:language" element={<RoleRoute roles={["super_admin"]}><PastoralContentEditor /></RoleRoute>} />
        <Route path="/admin/reading-plans" element={<RoleRoute roles={["super_admin"]}><ReadingPlanAdminList /></RoleRoute>} />
        <Route path="/admin/reading-plans/:planId" element={<RoleRoute roles={["super_admin"]}><ReadingPlanAdminDetail /></RoleRoute>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/about/explore-biblememos" element={<AboutApp />} />
        <Route path="/about/who-we-are" element={<WhoWeAre />} />
      </Routes>

      <div style={{ color: "black", backgroundColor: "white" }}>
        {/* Additional content or footer */}
      </div>
    </div>
  );
}

export default App;
